import React, { useState, useRef, useEffect } from "react";
import { RefreshCw } from "lucide-react";
import { LucideIcon } from "./icon-registry";

export const COLOR_PRESETS = [
    "#D1D5DB", "#EC4899", "#06B6D4", "#F59E0B", "#14B8A6", "#3B82F6", "#F97316",
    "#22C55E", "#C084FC", "#EF4444",
];

function randomHsl(): string {
    const h = Math.floor(Math.random() * 360);
    const s = 65, l = 55;
    const c = (1 - Math.abs(2 * l / 100 - 1)) * s / 100;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = l / 100 - c / 2;
    let [r, g, b] = [0, 0, 0];
    if (h < 60)       [r, g, b] = [c, x, 0];
    else if (h < 120) [r, g, b] = [x, c, 0];
    else if (h < 180) [r, g, b] = [0, c, x];
    else if (h < 240) [r, g, b] = [0, x, c];
    else if (h < 300) [r, g, b] = [x, 0, c];
    else              [r, g, b] = [c, 0, x];
    const toHex = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

interface Props { value: string; iconName: string | null; onChange: (hex: string) => void; }

export default function ColorPicker({ value, iconName, onChange }: Props) {
    const [open, setOpen] = useState(false);
    const [hexInput, setHexInput] = useState(value.toUpperCase());
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => { setHexInput(value.toUpperCase()); }, [value]);

    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const commitHex = (h: string) => {
        const normalized = h.startsWith("#") ? h : "#" + h;
        if (HEX_RE.test(normalized)) onChange(normalized.toUpperCase());
    };

    return (
        <div className="m-colorpicker" ref={wrapRef}>
            <button type="button" className="m-colorpicker-trigger" style={{ background: value }} onClick={() => setOpen(o => !o)} title="Pick color">
                <LucideIcon name={iconName} size={16} color="#fff" />
            </button>
            {open && (
                <div className="m-colorpicker-popover">
                    <div className="m-colorpicker-row">
                        {COLOR_PRESETS.slice(0, 7).map(c => (
                            <button type="button" key={c} className={`m-colorpicker-swatch${c === value.toUpperCase() ? " active" : ""}`}
                                style={{ background: c }} onClick={() => { onChange(c); setOpen(false); }} />
                        ))}
                    </div>
                    <div className="m-colorpicker-row">
                        {COLOR_PRESETS.slice(7).map(c => (
                            <button type="button" key={c} className={`m-colorpicker-swatch${c === value.toUpperCase() ? " active" : ""}`}
                                style={{ background: c }} onClick={() => { onChange(c); setOpen(false); }} />
                        ))}
                        <div className="m-colorpicker-hex">
                            <span className="m-colorpicker-hash">#</span>
                            <input
                                type="text" maxLength={6}
                                value={hexInput.replace(/^#/, "")}
                                onChange={e => {
                                    const v = e.target.value.toUpperCase();
                                    setHexInput("#" + v);
                                    commitHex("#" + v);
                                }}
                            />
                            <button type="button" className="m-colorpicker-random" onClick={() => onChange(randomHsl())} title="Randomize">
                                <RefreshCw size={13} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
